import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  ArrowLeft,
  Crown,
  User,
  ChevronDown,
  LogOut,
  RefreshCw,
  Mail,
  Settings,
  Plus,
  Edit,
  Trash2,
  Send,
  Eye,
  EyeOff,
  Key,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// Using relative URLs
const API = '/api';

const EmailManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [emailConfig, setEmailConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [activeTab, setActiveTab] = useState('configuration');
  
  const [configForm, setConfigForm] = useState({
    resend_api_key: '',
    from_email: '',
    from_name: ''
  });
  
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    html_content: '',
    text_content: '',
    variables: []
  });
  
  const [testEmailForm, setTestEmailForm] = useState({
    to: '',
    template_name: '',
    variables: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchEmailConfig(),
        fetchTemplates(),
        fetchEmailLogs()
      ]);
    } catch (error) {
      toast.error('Failed to load email management data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-configuration`);
      setEmailConfig(response.data);
      if (response.data.configured) {
        setConfigForm({
          resend_api_key: '',
          from_email: response.data.from_email || '',
          from_name: response.data.from_name || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-logs?limit=20`);
      setEmailLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
    }
  };

  const handleSaveConfig = async () => {
    if (!configForm.resend_api_key || !configForm.from_email || !configForm.from_name) {
      toast.error('Please fill in all configuration fields');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/email-configuration`, configForm);
      toast.success('Email configuration saved successfully');
      setShowConfigDialog(false);
      fetchEmailConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.html_content) {
      toast.error('Please fill in required fields (name, subject, html content)');
      return;
    }

    setActionLoading(true);
    try {
      if (selectedTemplate) {
        await axios.put(`${API}/admin/email-templates/${selectedTemplate.id}`, templateForm);
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${API}/admin/email-templates`, templateForm);
        toast.success('Template created successfully');
      }
      
      setShowTemplateDialog(false);
      setSelectedTemplate(null);
      resetTemplateForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete template "${templateName}"?`)) {
      return;
    }

    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/email-templates/${templateId}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailForm.to || !testEmailForm.template_name) {
      toast.error('Please enter email address and select a template');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/send-test-email`, testEmailForm);
      toast.success('Test email sent successfully');
      setShowTestEmailDialog(false);
      setTestEmailForm({ to: '', template_name: '', variables: {} });
      fetchEmailLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setActionLoading(false);
    }
  };

  const openCreateTemplate = () => {
    setSelectedTemplate(null);
    resetTemplateForm();
    setShowTemplateDialog(true);
  };

  const openEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      text_content: template.text_content || '',
      variables: template.variables || []
    });
    setShowTemplateDialog(true);
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      subject: '',
      html_content: '',
      text_content: '',
      variables: []
    });
  };

  const extractVariables = (content) => {
    const matches = content.match(/\{\{([^}]+)\}\}/g);
    return matches ? [...new Set(matches.map(match => match.replace(/[{}]/g, '')))] : [];
  };

  const updateVariables = () => {
    const htmlVars = extractVariables(templateForm.html_content);
    const textVars = extractVariables(templateForm.text_content);
    const subjectVars = extractVariables(templateForm.subject);
    const allVars = [...new Set([...htmlVars, ...textVars, ...subjectVars])];
    setTemplateForm(prev => ({ ...prev, variables: allVars }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Email Management</h1>
                  <p className="text-sm text-slate-600">Configure Resend and manage email templates</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={fetchData}
                className="border-slate-200 hover:bg-slate-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="max-w-[150px] truncate">{user?.full_name || user?.email?.split('@')[0]}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <p className="text-sm font-medium">{user?.full_name || user?.email}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => {
                      localStorage.clear();
                      window.location.href = '/login';
                    }}
                    className="text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-white rounded-lg p-1 mb-8 shadow-sm">
            {[
              { id: 'configuration', label: 'Configuration', icon: Settings },
              { id: 'templates', label: 'Templates', icon: FileText },
              { id: 'logs', label: 'Email Logs', icon: Clock }
            ].map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                className={`flex-1 ${activeTab === tab.id ? 'bg-navy-600 text-white' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </Button>
            ))}
          </div>

          {/* Configuration Tab */}
          {activeTab === 'configuration' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Email Service Configuration</CardTitle>
                      <CardDescription>Configure Resend API settings and default sender information</CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                      {emailConfig?.configured ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Configured
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Not Configured
                        </Badge>
                      )}
                      <Button onClick={() => setShowConfigDialog(true)}>
                        <Settings className="w-4 h-4 mr-2" />
                        Configure
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {emailConfig?.configured ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-600">From Email</Label>
                          <p className="text-slate-900">{emailConfig.from_email}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-600">From Name</Label>
                          <p className="text-slate-900">{emailConfig.from_name}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-600">API Key Status</Label>
                          <p className="text-slate-900">
                            {emailConfig.api_key_set ? (
                              <Badge className="bg-green-100 text-green-700">Configured</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Not Set</Badge>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Mail className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600 mb-4">Email service not configured yet</p>
                      <Button onClick={() => setShowConfigDialog(true)}>
                        <Settings className="w-4 h-4 mr-2" />
                        Configure Now
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Email Templates ({templates.length})</CardTitle>
                      <CardDescription>Manage email templates for different types of notifications</CardDescription>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => setShowTestEmailDialog(true)}
                        variant="outline"
                        disabled={!emailConfig?.configured}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Send Test Email
                      </Button>
                      <Button onClick={openCreateTemplate}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Template
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {templates.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600 mb-4">No email templates found</p>
                      <Button onClick={openCreateTemplate}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create First Template
                      </Button>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {templates.map((template) => (
                        <Card key={template.id} className="border border-slate-200">
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold text-slate-900 mb-1">{template.name}</h3>
                                <p className="text-sm text-slate-600 mb-3">{template.subject}</p>
                                <div className="flex flex-wrap gap-2">
                                  {template.variables?.map((variable) => (
                                    <Badge key={variable} variant="outline" className="text-xs">
                                      {variable}
                                    </Badge>
                                  ))}
                                </div>
                                <p className="text-xs text-slate-500 mt-3">
                                  Updated: {formatDate(template.updated_at)}
                                </p>
                              </div>
                              <div className="flex space-x-2 ml-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditTemplate(template)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteTemplate(template.id, template.name)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Email Logs Tab */}
          {activeTab === 'logs' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Email Logs ({emailLogs.length})</CardTitle>
                  <CardDescription>Recent email sending activity</CardDescription>
                </CardHeader>
                <CardContent>
                  {emailLogs.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No emails sent yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {emailLogs.map((log) => (
                        <div key={log.id} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-slate-900">{log.subject}</h4>
                              <p className="text-sm text-slate-600">To: {log.to}</p>
                              <p className="text-sm text-slate-600">Template: {log.template_name}</p>
                              <p className="text-xs text-slate-500">
                                Sent: {formatDate(log.sent_at)}
                              </p>
                            </div>
                            <Badge className="bg-green-100 text-green-700">
                              {log.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Email Configuration</DialogTitle>
            <DialogDescription>
              Configure Resend API settings for sending emails
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="api_key">Resend API Key</Label>
              <div className="relative">
                <Input
                  id="api_key"
                  type={showApiKey ? "text" : "password"}
                  value={configForm.resend_api_key}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, resend_api_key: e.target.value }))}
                  placeholder="re_..."
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="from_email">From Email</Label>
              <Input
                id="from_email"
                type="email"
                value={configForm.from_email}
                onChange={(e) => setConfigForm(prev => ({ ...prev, from_email: e.target.value }))}
                placeholder="noreply@teamnova.ai"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="from_name">From Name</Label>
              <Input
                id="from_name"
                value={configForm.from_name}
                onChange={(e) => setConfigForm(prev => ({ ...prev, from_name: e.target.value }))}
                placeholder="Nova AI"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} disabled={actionLoading}>
              {actionLoading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              Create or modify email templates with variable support
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="template_name">Template Name</Label>
                <Input
                  id="template_name"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="password-reset"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Reset Your Password - {{app_name}}"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="html_content">HTML Content</Label>
              <Textarea
                id="html_content"
                value={templateForm.html_content}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, html_content: e.target.value }))}
                placeholder="<h1>Hello {{user_name}}</h1>"
                className="min-h-[200px] font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="text_content">Text Content (Optional)</Label>
              <Textarea
                id="text_content"
                value={templateForm.text_content}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, text_content: e.target.value }))}
                placeholder="Hello {{user_name}}..."
                className="min-h-[100px] font-mono text-sm"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Variables</Label>
                <p className="text-xs text-slate-500">Use {`{{variable_name}}`} syntax</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={updateVariables}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Extract Variables
              </Button>
            </div>

            {templateForm.variables.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {templateForm.variables.map((variable) => (
                  <Badge key={variable} variant="outline">
                    {variable}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={actionLoading}>
              {actionLoading ? 'Saving...' : selectedTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={showTestEmailDialog} onOpenChange={setShowTestEmailDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email using one of your templates
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="test_email">Email Address</Label>
              <Input
                id="test_email"
                type="email"
                value={testEmailForm.to}
                onChange={(e) => setTestEmailForm(prev => ({ ...prev, to: e.target.value }))}
                placeholder="test@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="test_template">Template</Label>
              <select
                id="test_template"
                value={testEmailForm.template_name}
                onChange={(e) => setTestEmailForm(prev => ({ ...prev, template_name: e.target.value }))}
                className="w-full p-2 border border-slate-200 rounded-md"
              >
                <option value="">Select a template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.name}>
                    {template.name} - {template.subject}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Test Variables (JSON)</Label>
              <Textarea
                value={JSON.stringify(testEmailForm.variables, null, 2)}
                onChange={(e) => {
                  try {
                    const variables = JSON.parse(e.target.value);
                    setTestEmailForm(prev => ({ ...prev, variables }));
                  } catch (error) {
                    // Invalid JSON, ignore
                  }
                }}
                placeholder='{"user_name": "John Doe", "app_name": "Nova AI"}'
                className="min-h-[100px] font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestEmailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendTestEmail} disabled={actionLoading}>
              <Send className="w-4 h-4 mr-2" />
              {actionLoading ? 'Sending...' : 'Send Test Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailManagement;